import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import type { InvoiceSummary } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate, formatEur } from '../lib/format';
import { Button, Card, Chip, Empty, Loading, PageHeader, invoiceStatusLabel, invoiceTone, vatShort } from '../components/ui';

const FILTERS = ['all', 'draft', 'sent', 'overdue', 'in_dunning', 'paid'] as const;
const FILTER_LABEL: Record<string, string> = { all: 'All', draft: 'Draft', sent: 'Sent', overdue: 'Overdue', in_dunning: 'In dunning', paid: 'Paid' };

export function Invoices() {
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'all';
  const search = params.get('q') ?? '';
  const q = useQuery({
    queryKey: ['invoices', company?.id, status, search],
    queryFn: () => capi<{ items: InvoiceSummary[]; count: number; total: number }>(`/invoices?status=${status}&q=${encodeURIComponent(search)}&limit=200`),
  });
  return (
    <>
      <PageHeader crumb={t('invCrumb')} title={t('invTitle')} sub={t('invSub')}
        actions={<><Button icon="fas fa-download" onClick={() => window.open(`/api/companies/${company?.id}/invoices?status=${status}&limit=200`, '_blank')}>{t('btnExport')}</Button><Button icon="fas fa-file-import" onClick={() => navigate('/invoices/import')}>{t('importInvoice')}</Button><Button variant="primary" icon="fas fa-plus" onClick={() => navigate('/invoices/new')}>{t('invAction')}</Button></>} />
      <div className="stack">
        <div className="row">
          {FILTERS.map((f) => <button key={f} type="button" className={`pill ${status === f ? 'active' : ''}`} style={{ background: status === f ? undefined : 'var(--btn-bg)', borderColor: status === f ? undefined : 'var(--glass-border)' }} onClick={() => setParams((p) => { p.set('status', f); return p; })}>{s(FILTER_LABEL[f]!)}</button>)}
          {search && <button type="button" className="pill active" onClick={() => setParams((p) => { p.delete('q'); return p; })}>“{search}” <i className="fas fa-xmark" /></button>}
          <div className="spacer" />
          {q.data && <span className="small muted">{t('ofInvoices', { count: q.data.count, total: q.data.total })}</span>}
        </div>
        <Card pad={false} className="table">
          {!q.data ? <Loading /> : (
            <table>
              <thead><tr><th>{t('colInvoice')}</th><th>{t('colCustomer')}</th><th>{t('colIssued')}</th><th>{t('colDue')}</th><th>{t('colVat')}</th><th>{t('colStatus')}</th><th className="r">{t('colAmount')}</th></tr></thead>
              <tbody>
                {q.data.items.length === 0 && <Empty />}
                {q.data.items.map((r) => (
                  <tr key={r.id} className="click" onClick={() => navigate(`/invoices/${r.id}`)}>
                    <td className="ref">{r.status === 'draft' ? s('Draft') : r.number}</td>
                    <td style={{ fontWeight: 600 }}>{r.customerName}</td>
                    <td className="secondary small">{formatDate(r.issuedAt, lang)}</td>
                    <td className="secondary small">{formatDate(r.dueAt, lang)}</td>
                    <td className="secondary small">{s(vatShort(r.vatTreatment))}</td>
                    <td><Chip tone={invoiceTone(r.status)}>{r.status === 'paid' && r.paidAt ? `${s('Paid')} ${formatDate(r.paidAt, lang).slice(0, 6)}` : s(invoiceStatusLabel(r.status))}</Chip></td>
                    <td className="amount">{formatEur(r.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
